import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import useAuth from 'src/hooks/useAuth';
import useHasPermissions from 'src/hooks/useHasPermission';
import {
  CLIENT_EDR_SOC,
  CLIENT_MINDTIME_SECURITY,
  CLIENT_MINDTIME_SECURITY_EDR,
} from 'src/constants/constants';
import { permissionBelongsToSubscriptionRegistry, registryModuleDefinesComponents } from 'src/mindtime/config/subscriptionPermissionRegistry';
import { useSubscriptionPermissionsContext } from 'src/mindtime/contexts/SubscriptionPermissionsContext';
import {
  buildSubscriptionAccessContext,
  canAccess,
  hasAnyRolePermission,
  hasGranularModuleChildEntitlements,
  hasSubscriptionEntitlement,
  isRoleOnlyEdrPermission,
  resolveClientSubscribedAccess,
  resolveEdrPermissionsReady,
} from 'src/mindtime/utils/subscriptionPermissions';
import { useIsResellerOrg, useOrganizationTypeName } from 'src/mindtime/hooks/useIsResellerOrg';
import { SubscriptionUnavailablePage } from 'src/mindtime/pages/security/edr/shared/SubscriptionUnavailablePage';
import { SubscriptionPermissionsPageSkeleton } from 'src/mindtime/pages/security/edr/shared/SubscriptionPermissionsSkeleton';
import {
  getSubscriptionUnavailableMessageKey,
  SUBSCRIPTION_UNAVAILABLE_PREVIEW_CREATE_KEY,
} from 'src/mindtime/pages/security/edr/shared/subscriptionUnavailable';

const CREATE_SUFFIX = '-create';

/** Reseller JWT: Client Mindtime Security → EDR/SOC group before module/widgets. */
const hasResellerSecurityHierarchy = (
  checkRole: (permissions: string[]) => boolean,
  permission: string,
): boolean => {
  if (!hasAnyRolePermission(checkRole, CLIENT_MINDTIME_SECURITY)) return false;

  const normalized = String(permission ?? '').trim().toLowerCase();
  const isSoc =
    normalized === CLIENT_EDR_SOC.toLowerCase() || normalized.startsWith('client_edr_soc_');
  if (isSoc) return hasAnyRolePermission(checkRole, CLIENT_EDR_SOC);

  if (normalized.startsWith('client_edr_')) {
    return hasAnyRolePermission(checkRole, CLIENT_MINDTIME_SECURITY_EDR);
  }

  return true;
};

type SubscriptionProductPermissionGuardProps = {
  hasPermission: string;
  unavailableKey?: string;
  children: ReactNode;
};

const SubscriptionProductPermissionGuard = ({
  hasPermission,
  unavailableKey,
  children,
}: SubscriptionProductPermissionGuardProps) => {
  const checkRole = useHasPermissions();
  const { isInitialized, user } = useAuth();
  const { orgEntitlements, hasActiveSubscription, subscriptionKeys, roleKeys, resolved } =
    useSubscriptionPermissionsContext();
  const isReseller = useIsResellerOrg();
  const organizationTypeName = useOrganizationTypeName();

  const clientSubscribed = resolveClientSubscribedAccess(
    isReseller,
    orgEntitlements,
    hasActiveSubscription,
  );

  const permissionsReady = resolveEdrPermissionsReady({
    isReseller,
    authInitialized: isInitialized,
    hasAuthUser: Boolean(user),
    subscriptionResolved: resolved,
  });

  if (!permissionsReady) return <SubscriptionPermissionsPageSkeleton />;

  const isProductPermission = permissionBelongsToSubscriptionRegistry(hasPermission);

  const accessContext = buildSubscriptionAccessContext({
    isReseller,
    organizationTypeName,
    orgEntitlements,
    hasActiveSubscription,
    roleKeys,
    subscriptionKeys,
    checkRole,
    useSubscriptionEntitlements: isProductPermission,
  });

  const allowed = canAccess(accessContext, hasPermission, { blockPreviewCreate: true });

  // Reseller/preview / role-only modules (EDR Clients): when widgets are registered, require ≥1 child.
  // Leaf modules (e.g. SOC) have no components — module `-read` alone is enough.
  const isRoleTier = accessContext.tier !== 'subscribed';
  const isCreate = hasPermission.toLowerCase().endsWith(CREATE_SUFFIX);
  const forceRoleKeys = isRoleOnlyEdrPermission(hasPermission);
  const hasModuleContent =
    (!isRoleTier && !forceRoleKeys) ||
    !isProductPermission ||
    isCreate ||
    !registryModuleDefinesComponents(hasPermission) ||
    hasGranularModuleChildEntitlements(
      forceRoleKeys ? accessContext.roleKeys : accessContext.effectiveKeys,
      hasPermission,
    );

  const hasHierarchy =
    accessContext.tier !== 'reseller' ||
    !isProductPermission ||
    hasResellerSecurityHierarchy(checkRole, hasPermission);

  if (!allowed || !hasModuleContent || !hasHierarchy) {
    const chromeKey = unavailableKey ?? getSubscriptionUnavailableMessageKey(hasPermission);
    const previewCreateBlocked =
      accessContext.tier === 'preview' &&
      isCreate &&
      canAccess(accessContext, hasPermission);

    if (previewCreateBlocked) {
      return (
        <SubscriptionUnavailablePage
          messageKey={SUBSCRIPTION_UNAVAILABLE_PREVIEW_CREATE_KEY}
          chromeKey={chromeKey}
        />
      );
    }

    // Subscribed: subscription miss → message; subscription ok but role/hierarchy miss → 404.
    // Reseller / preview / role-only → always 404.
    const subscriptionDenied =
      accessContext.tier === 'subscribed' &&
      !forceRoleKeys &&
      !hasSubscriptionEntitlement(accessContext, hasPermission);

    if (subscriptionDenied) {
      return <SubscriptionUnavailablePage messageKey={chromeKey} chromeKey={chromeKey} />;
    }

    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
};

export default SubscriptionProductPermissionGuard;
